import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/lib/utils';
import { legacyRoutes } from '@/lib/legacy';
import type { NetworkDevice, DeviceStatus, RouteRecord } from '../types';

interface DeviceDetailPanelProps {
  device: NetworkDevice | null;
  routeRecords: RouteRecord[];
  onClose: () => void;
}

const STATUS_BADGE: Record<DeviceStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' }> = {
  online: { label: 'Online', variant: 'default' },
  stale: { label: 'Stale', variant: 'secondary' },
  offline: { label: 'Offline', variant: 'destructive' },
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function DeviceDetailPanel({ device, routeRecords, onClose }: DeviceDetailPanelProps) {
  if (!device) return null;

  const deviceRoutes = routeRecords.filter(r => r.sourceSl === device.xbee_SL);

  const badge = STATUS_BADGE[device.status];
  const primaryGateway = device.current_parent_gateways[0] ?? null;

  return (
    <div className="border-t border-border bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground">{device.name}</h3>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <span className="text-muted-foreground">Status</span>
          <div className="mt-0.5">
            <Badge variant={badge.variant}>{badge.label}</Badge>
          </div>
        </div>

        <div>
          <span className="text-muted-foreground">Device ID</span>
          <p className="font-mono text-xs mt-0.5">{device.dev_id}</p>
        </div>

        {device.xbee_SL && (
          <div>
            <span className="text-muted-foreground">XBee SL</span>
            <p className="font-mono text-xs mt-0.5">{device.xbee_SL}</p>
          </div>
        )}

        <div>
          <span className="text-muted-foreground">Last Packet</span>
          <p className="mt-0.5">{timeAgo(device.lastpacket_at)}</p>
          {device.lastpacket_at && (
            <p className="text-xs text-muted-foreground">{formatDateTime(device.lastpacket_at)}</p>
          )}
        </div>

        {device.battery_voltage != null && (
          <div>
            <span className="text-muted-foreground">Battery</span>
            <p className="mt-0.5">
              {device.battery_voltage}V
              {device.battery_percentage != null && ` (${device.battery_percentage}%)`}
            </p>
          </div>
        )}

        <div>
          <span className="text-muted-foreground">Sensors</span>
          <p className="mt-0.5">{device.sensor_count}</p>
        </div>

        {primaryGateway && (
          <div>
            <span className="text-muted-foreground">Parent Gateway</span>
            <p className="font-mono text-xs mt-0.5">{primaryGateway.mac}</p>
            <p className="text-xs text-muted-foreground">
              Signal: {primaryGateway.rssi}/7
              {' '}({primaryGateway.rssi >= 6 ? 'Excellent' : primaryGateway.rssi >= 4 ? 'Good' : primaryGateway.rssi >= 2 ? 'Weak' : 'Critical'})
            </p>
          </div>
        )}

        <div>
          <span className="text-muted-foreground">Role</span>
          <p className="mt-0.5">
            {device.is_coordinator ? 'Coordinator' : device.current_parent_gateways.length > 0 ? 'End Device' : 'Unknown'}
          </p>
        </div>
      </div>

      <div className="flex gap-2 mt-3">
        <a href={legacyRoutes.sensor(device._id)} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm">View Transmitter</Button>
        </a>
      </div>

      {/* Route History */}
      {deviceRoutes.length > 0 && (
        <div className="mt-3 border-t border-border pt-3">
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">Route History</h4>
          <div className="space-y-1">
            {deviceRoutes.slice(0, 5).map((route) => (
              <div key={route.id} className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground w-16 shrink-0">{timeAgo(route.recordedAt)}</span>
                <span className="font-mono">
                  {route.relayPath.length === 0
                    ? 'Direct'
                    : route.relayPath.join(' → ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
